import React, { useState, useEffect, useCallback } from "react";
import { Modal, Tabs, message, Tooltip } from "antd";
import { toast } from "react-toastify";
import {
  fetchLetterTemplate,
  saveLetterTemplate,
  resetLetterTemplate,
  fetchAllotmentLetterPreview,
  downloadAllotmentLetterPdf,
  AllotmentLetterFormValues,
} from "../services/staffQuarterLetters.service";

interface AllotmentLetterCustomizerModalProps {
  open: boolean;
  onClose: () => void;
  allotmentData?: {
    allotmentId?: string | number;
    registrationId?: string | number;
    employeeName?: string;
    employee_name?: string;
    employeeNumber?: string;
    empId?: string;
    emp_id?: string;
    quarterType?: string;
    quarter_type?: string;
    flatNumber?: string;
    flatNo?: string;
    flat_number?: string;
    location?: string;
    location_name?: string;
    colony_name?: string;
    pool?: string;
    poolName?: string;
    date?: string;
    referenceNumber?: string;
    orderNo?: string;
    category?: string;
    [key: string]: any;
  } | null;
}

const DEFAULT_TERMS: string[] = [
  "आबंटित क्वार्टर का आधिपत्य (Possession) इस आदेश के जारी होने की तिथि से {{possessionDate}} दिनों के भीतर लेना अनिवार्य होगा।",
  "यदि कर्मचारी निर्धारित अवधि में आधिपत्य नहीं लेता है, तो यह आबंटन स्वतः रद्द माना जाएगा तथा नियमानुसार HRA की कटौती की जाएगी।",
  "क्वार्टर का उपयोग केवल स्वयं एवं अपने अधिकृत परिवार के सदस्यों के निवास हेतु ही किया जाएगा। इसे किसी अन्य को उप-किराए (Sublet) पर देना पूर्णतः वर्जित है।",
  "विद्युत, जल एवं अन्य उपयोगिता देयों (Utility Charges) का भुगतान कर्मचारी द्वारा सीधे संबंधित प्राधिकरण/विभाग को समय पर किया जाएगा।",
  "क्वार्टर के रख-रखाव एवं स्वच्छता का पूर्ण ध्यान रखना होगा। परिसर में किसी भी प्रकार का अनाधिकृत निर्माण या संरचनात्मक परिवर्तन नहीं किया जा सकेगा।",
  "स्थानांतरण, त्यागपत्र, सेवानिवृत्ति या सेवा समाप्ति की स्थिति में DMRC क्वार्टर आबंटन नियमों के तहत निर्धारित समय-सीमा के भीतर क्वार्टर रिक्त करना अनिवार्य होगा।",
  "कर्मचारी DMRC स्टाफ क्वार्टर नियम-2025 तथा समय-समय पर जारी होने वाले समस्त दिशा-निर्देशों एवं संशोधनों का पालन करने के लिए बाध्य होगा।",
];

const DEFAULT_COPY_TO: string[] = [
  "संबंधित कर्मचारी: श्री/श्रीमती {{employeeName}} (कर्मचारी सं.: {{employeeId}})",
  "वरिष्ठ प्रबंधक/मा.सं./स्टाफ क्वार्टर कक्ष",
  "प्रबंधक/वित्त (वेतन एवं भत्ते)",
  "सहायक प्रबंधक/सिविल अनुरक्षण",
  "सहायक प्रबंधक/विद्युत अनुरक्षण",
  "सुरक्षा प्रभारी / संबंधित आवासीय कॉलोनी",
];

const DEFAULT_CC = "सीसी/- सूचना पट्ट / व्यक्तिगत पत्रावली / कार्यालय प्रति";

const formatDateDDMMYYYY = (dateInput?: any) => {
  const d = dateInput ? new Date(dateInput) : new Date();
  if (isNaN(d.getTime())) return new Date().toLocaleDateString("en-GB").replace(/\//g, ".");
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
};

const AllotmentLetterCustomizerModal: React.FC<AllotmentLetterCustomizerModalProps> = ({
  open,
  onClose,
  allotmentData,
}) => {
  const [activeTabKey, setActiveTabKey] = useState<string>("edit");
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string>("");

  // Form State
  const [formData, setFormData] = useState<AllotmentLetterFormValues>({
    orderDate: formatDateDDMMYYYY(),
    orderNo: "102",
    financialYear: "2026-27",
    fileNumberPrefix: "DMRC/HR/SQ/Allotment",
    rulesReferenceDate: "03.06.2025",
    possessionDeadlineDays: 21,
    employeeName: "",
    employeeId: "",
    pool: "OPN",
    quarterType: "II",
    flatNumber: "",
    location: "",
    category: "नियमित",
    terms: [...DEFAULT_TERMS],
    signatoryName: "(अशोक कुमार)",
    signatoryDesignation: "प्रबंधक/मा.सं./परि.एवं.अनु",
    copyToList: [...DEFAULT_COPY_TO],
    ccTo: DEFAULT_CC,
  });

  // Initialize form data when allotmentData changes
  useEffect(() => {
    if (open) {
      const empName =
        allotmentData?.employeeName ||
        allotmentData?.employee_name ||
        allotmentData?.name ||
        "";
      const empId =
        allotmentData?.employeeNumber ||
        allotmentData?.empId ||
        allotmentData?.emp_id ||
        "";
      const flat =
        allotmentData?.flatNumber ||
        allotmentData?.flatNo ||
        allotmentData?.flat_number ||
        "";
      const loc =
        allotmentData?.location ||
        allotmentData?.location_name ||
        allotmentData?.colony_name ||
        "";
      const qType =
        allotmentData?.quarterType ||
        allotmentData?.quarter_type ||
        "II";
      const pool =
        allotmentData?.pool ||
        allotmentData?.poolName ||
        "OPN";
      const orderNo =
        allotmentData?.orderNo ||
        allotmentData?.referenceNumber?.replace(/\D/g, "")?.slice(-4) ||
        "102";

      setFormData((prev) => ({
        ...prev,
        allotmentId: allotmentData?.allotmentId || allotmentData?.id || undefined,
        registrationId: allotmentData?.registrationId || undefined,
        employeeName: empName || prev.employeeName,
        employeeId: empId || prev.employeeId,
        flatNumber: flat || prev.flatNumber,
        location: loc || prev.location,
        quarterType: qType || prev.quarterType,
        pool: pool || prev.pool,
        orderNo: orderNo || prev.orderNo,
        orderDate: allotmentData?.date ? formatDateDDMMYYYY(allotmentData.date) : prev.orderDate,
      }));

      // Load saved template from backend
      loadTemplate();
    }
  }, [open, allotmentData]);

  const loadTemplate = async () => {
    setLoadingTemplate(true);
    try {
      const res = await fetchLetterTemplate("ALLOTMENT_LETTER");
      const tpl = res?.data?.data || res?.data || res;
      if (tpl && typeof tpl === "object") {
        const tplTerms = (Array.isArray(tpl.terms) && tpl.terms.length > 0)
          ? tpl.terms
          : (Array.isArray(tpl.termsAndConditions) && tpl.termsAndConditions.length > 0)
          ? tpl.termsAndConditions
          : undefined;

        const tplCopyToList = (Array.isArray(tpl.copyToList) && tpl.copyToList.length > 0)
          ? tpl.copyToList
          : (Array.isArray(tpl.copyTo) && tpl.copyTo.length > 0)
          ? tpl.copyTo
          : undefined;

        const tplCcTo = tpl.ccTo !== undefined
          ? tpl.ccTo
          : (tpl.additionalNotes !== undefined ? tpl.additionalNotes : undefined);

        const tplPrefix = tpl.fileNumberPrefix || tpl.refNumberPrefix || tpl.fileReferencePrefix;
        const tplRulesDate = tpl.rulesReferenceDate || tpl.rulesReference;
        const tplDeadline = tpl.possessionDeadlineDays || tpl.possessionDaysAllowed;
        const tplCategory = tpl.allotmentCategoryDefault || tpl.category;

        setFormData((prev) => ({
          ...prev,
          signatoryName: tpl.signatoryName || prev.signatoryName,
          signatoryDesignation: tpl.signatoryDesignation || prev.signatoryDesignation,
          terms: tplTerms || prev.terms,
          copyToList: tplCopyToList || prev.copyToList,
          ccTo: tplCcTo !== undefined ? tplCcTo : prev.ccTo,
          rulesReferenceDate: tplRulesDate || prev.rulesReferenceDate,
          possessionDeadlineDays: tplDeadline || prev.possessionDeadlineDays,
          fileNumberPrefix: tplPrefix || prev.fileNumberPrefix,
          category: tplCategory || prev.category,
        }));
      }
    } catch (err) {
      // Template fetch silently fallback to defaults
    } finally {
      setLoadingTemplate(false);
    }
  };

  const handleInputChange = (field: keyof AllotmentLetterFormValues, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleTermChange = (index: number, value: string) => {
    setFormData((prev) => {
      const newTerms = [...(prev.terms || DEFAULT_TERMS)];
      newTerms[index] = value;
      return { ...prev, terms: newTerms };
    });
  };

  const handleAddTerm = () => {
    setFormData((prev) => ({
      ...prev,
      terms: [...(prev.terms || []), ""],
    }));
  };

  const handleRemoveTerm = (index: number) => {
    setFormData((prev) => {
      const newTerms = (prev.terms || []).filter((_, i) => i !== index);
      return { ...prev, terms: newTerms };
    });
  };

  const handleCopyItemChange = (index: number, value: string) => {
    setFormData((prev) => {
      const newList = [...(prev.copyToList || DEFAULT_COPY_TO)];
      newList[index] = value;
      return { ...prev, copyToList: newList };
    });
  };

  const handleAddCopyItem = () => {
    setFormData((prev) => ({
      ...prev,
      copyToList: [...(prev.copyToList || []), ""],
    }));
  };

  const handleRemoveCopyItem = (index: number) => {
    setFormData((prev) => {
      const newList = (prev.copyToList || []).filter((_, i) => i !== index);
      return { ...prev, copyToList: newList };
    });
  };

  const fetchPreview = useCallback(async () => {
    setPreviewLoading(true);
    try {
      const res = await fetchAllotmentLetterPreview(formData);
      const html = res?.data?.renderedHtml || res?.renderedHtml || res?.html || "";
      if (html) {
        setPreviewHtml(html);
      } else {
        // Build rich client-side preview if backend only returns metadata
        buildClientSidePreview();
      }
    } catch (error) {
      buildClientSidePreview();
    } finally {
      setPreviewLoading(false);
    }
  }, [formData]);

  const buildClientSidePreview = () => {
    const termsHtml = (formData.terms || DEFAULT_TERMS)
      .map(
        (t, i) =>
          `<li style="margin-bottom: 8px; line-height: 1.6;">${t.replace(
            /\{\{possessionDate\}\}/g,
            String(formData.possessionDeadlineDays || 21)
          )}</li>`
      )
      .join("");

    const copyToHtml = (formData.copyToList || DEFAULT_COPY_TO)
      .map(
        (c, i) =>
          `<li style="margin-bottom: 4px; line-height: 1.5;">${c
            .replace(/\{\{employeeName\}\}/g, formData.employeeName || "")
            .replace(/\{\{employeeId\}\}/g, formData.employeeId || "")}</li>`
      )
      .join("");

    const mockHtml = `
      <div style="font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; color: #1e293b; background: #fff; padding: 40px; border-radius: 8px; box-shadow: 0 4px 20px rgba(0,0,0,0.06); max-width: 800px; margin: 0 auto;">
        <!-- Header -->
        <div style="text-align: center; border-bottom: 2px solid #e2e8f0; padding-bottom: 16px; margin-bottom: 20px;">
          <h2 style="color: #c2410c; margin: 0; font-size: 20px; font-weight: 800; letter-spacing: 0.5px;">दिल्ली मेट्रो रेल कॉर्पोरेशन लिमिटेड</h2>
          <h3 style="color: #475569; margin: 4px 0 0; font-size: 16px; font-weight: 600;">DELHI METRO RAIL CORPORATION LTD.</h3>
          <p style="color: #64748b; margin: 4px 0 0; font-size: 12px;">(भारत सरकार एवं दिल्ली सरकार का संयुक्त उपक्रम)</p>
          <div style="margin-top: 12px; display: inline-block; background: #fef3c7; color: #92400e; padding: 4px 16px; border-radius: 20px; font-weight: 700; font-size: 14px; border: 1px solid #fde68a;">
            कार्यालय आदेश / OFFICE ORDER (आबंटन संख्या: ${formData.orderNo || "102"})
          </div>
        </div>

        <!-- Meta Bar -->
        <div style="display: flex; justify-content: space-between; font-size: 13px; color: #475569; margin-bottom: 20px; border-bottom: 1px dashed #cbd5e1; padding-bottom: 10px;">
          <div><strong>पत्रांक:</strong> ${formData.fileNumberPrefix || "DMRC/HR/SQ"}/${formData.financialYear || "2026-27"}/${formData.orderNo || "102"}</div>
          <div><strong>दिनांक:</strong> ${formData.orderDate || formatDateDDMMYYYY()}</div>
        </div>

        <!-- Subject -->
        <div style="background: #f8fafc; border-left: 4px solid #c2410c; padding: 12px 16px; border-radius: 0 8px 8px 0; margin-bottom: 24px;">
          <p style="margin: 0; font-size: 14px; font-weight: 700; color: #0f172a;">
            विषय: DMRC स्टाफ क्वार्टर आबंटन नियम-${formData.rulesReferenceDate || "03.06.2025"} के अंतर्गत आवासीय क्वार्टर का आबंटन।
          </p>
        </div>

        <!-- Allotment Table -->
        <table style="width: 100%; border-collapse: collapse; margin-bottom: 24px; font-size: 13px;">
          <thead>
            <tr style="background: #f1f5f9; color: #334155;">
              <th style="border: 1px solid #cbd5e1; padding: 8px 12px; text-align: left;">कर्मचारी का नाम</th>
              <th style="border: 1px solid #cbd5e1; padding: 8px 12px; text-align: center;">कर्मचारी सं.</th>
              <th style="border: 1px solid #cbd5e1; padding: 8px 12px; text-align: center;">पूल</th>
              <th style="border: 1px solid #cbd5e1; padding: 8px 12px; text-align: center;">प्रकार</th>
              <th style="border: 1px solid #cbd5e1; padding: 8px 12px; text-align: center;">फ्लैट सं.</th>
              <th style="border: 1px solid #cbd5e1; padding: 8px 12px; text-align: left;">स्थान / कॉलोनी</th>
              <th style="border: 1px solid #cbd5e1; padding: 8px 12px; text-align: center;">श्रेणी</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style="border: 1px solid #cbd5e1; padding: 10px 12px; font-weight: 700; color: #0f172a;">${formData.employeeName || "N/A"}</td>
              <td style="border: 1px solid #cbd5e1; padding: 10px 12px; text-align: center; font-weight: 600;">${formData.employeeId || "N/A"}</td>
              <td style="border: 1px solid #cbd5e1; padding: 10px 12px; text-align: center;">${formData.pool || "OPN"}</td>
              <td style="border: 1px solid #cbd5e1; padding: 10px 12px; text-align: center; font-weight: 600; color: #c2410c;">${formData.quarterType || "II"}</td>
              <td style="border: 1px solid #cbd5e1; padding: 10px 12px; text-align: center; font-weight: 700; color: #15803d; background: #f0fdf4;">${formData.flatNumber || "N/A"}</td>
              <td style="border: 1px solid #cbd5e1; padding: 10px 12px;">${formData.location || "N/A"}</td>
              <td style="border: 1px solid #cbd5e1; padding: 10px 12px; text-align: center;">${formData.category || "नियमित"}</td>
            </tr>
          </tbody>
        </table>

        <!-- Terms & Conditions Box -->
        <div style="border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px 20px; background: #ffffff; margin-bottom: 24px;">
          <h4 style="margin: 0 0 12px; font-size: 14px; color: #0f172a; font-weight: 700; text-decoration: underline;">आबंटन के नियम एवं शर्तें:</h4>
          <ol style="margin: 0; padding-left: 20px; font-size: 13px; color: #334155;">
            ${termsHtml}
          </ol>
        </div>

        <!-- Signatory Block -->
        <div style="display: flex; justify-content: flex-end; margin-top: 30px; margin-bottom: 24px;">
          <div style="text-align: center; min-width: 220px;">
            <div style="font-weight: 700; font-size: 14px; color: #0f172a;">${formData.signatoryName || "(अशोक कुमार)"}</div>
            <div style="font-size: 13px; color: #475569;">${formData.signatoryDesignation || "प्रबंधक/मा.सं./परि.एवं.अनु"}</div>
            <div style="font-size: 12px; color: #64748b; margin-top: 4px;">कृते दिल्ली मेट्रो रेल कॉर्पोरेशन लिमिटेड</div>
          </div>
        </div>

        <!-- Distribution / Copy To -->
        <div style="border-top: 1px solid #e2e8f0; padding-top: 16px; font-size: 12px; color: #475569;">
          <div style="font-weight: 700; color: #0f172a; margin-bottom: 8px;">प्रतिलिपि सूचनार्थ एवं आवश्यक कार्यवाही हेतु प्रेषित:</div>
          <ol style="margin: 0 0 8px; padding-left: 18px;">
            ${copyToHtml}
          </ol>
          ${formData.ccTo ? `<div style="font-style: italic; color: #64748b; margin-top: 6px;">${formData.ccTo}</div>` : ""}
        </div>
      </div>
    `;
    setPreviewHtml(mockHtml);
  };

  useEffect(() => {
    if (activeTabKey === "preview") {
      fetchPreview();
    }
  }, [activeTabKey, fetchPreview]);

  const handleSaveAsTemplate = async () => {
    setSavingTemplate(true);
    try {
      await saveLetterTemplate("ALLOTMENT_LETTER", {
        signatoryName: formData.signatoryName,
        signatoryDesignation: formData.signatoryDesignation,
        terms: formData.terms,
        copyToList: formData.copyToList,
        ccTo: formData.ccTo,
      });
      toast.success("Allotment Letter template saved to database successfully!");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to save template.");
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleResetDefaults = async () => {
    Modal.confirm({
      title: "Reset Template to DMRC Defaults?",
      content: "This will restore all terms, conditions, and signatories to the standard DMRC rules.",
      okText: "Reset to Default",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await resetLetterTemplate("ALLOTMENT_LETTER");
          setFormData((prev) => ({
            ...prev,
            orderDate: formatDateDDMMYYYY(),
            orderNo: "102",
            financialYear: "2026-27",
            rulesReferenceDate: "03.06.2025",
            possessionDeadlineDays: 21,
            category: "नियमित",
            terms: [...DEFAULT_TERMS],
            signatoryName: "(अशोक कुमार)",
            signatoryDesignation: "प्रबंधक/मा.सं./परि.एवं.अनु",
            copyToList: [...DEFAULT_COPY_TO],
            ccTo: DEFAULT_CC,
          }));
          toast.success("Template reset to standard DMRC rules successfully!");
          if (activeTabKey === "preview") {
            setTimeout(() => fetchPreview(), 200);
          }
        } catch (err: any) {
          toast.error("Failed to reset template.");
        }
      },
    });
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      await downloadAllotmentLetterPdf(formData);
      toast.success("Official Allotment Letter PDF downloaded successfully!");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to generate official PDF.");
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={1000}
      centered
      footer={null}
      destroyOnClose
      style={{ top: 20 }}
      styles={{
        body: { padding: "0 0 16px 0", maxHeight: "82vh", overflowY: "auto" },
      }}
      title={
        <div className="d-flex align-items-center justify-content-between pe-4 py-2 border-bottom">
          <div className="d-flex align-items-center gap-3">
            <div
              className="d-flex align-items-center justify-content-center rounded-3"
              style={{
                width: 44,
                height: 44,
                background: "linear-gradient(135deg, #c2410c 0%, #ea580c 100%)",
                color: "#fff",
                boxShadow: "0 4px 10px rgba(194, 65, 12, 0.25)",
              }}
            >
              <i className="ti ti-file-certificate" style={{ fontSize: 22 }} />
            </div>
            <div>
              <h5 className="mb-0 fw-bold text-dark" style={{ fontSize: 17 }}>
                Staff Quarters Allotment Order / कार्यालय आदेश
              </h5>
              <p className="text-muted mb-0 small">
                Customize terms, signatory, and live preview before issuing official PDF
              </p>
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            <button
              className={`btn btn-sm px-3 rounded-pill fw-semibold ${
                activeTabKey === "edit"
                  ? "btn-primary shadow-xs"
                  : "btn-outline-secondary"
              }`}
              onClick={() => setActiveTabKey("edit")}
            >
              <i className="ti ti-edit me-1" /> Edit Content
            </button>
            <button
              className={`btn btn-sm px-3 rounded-pill fw-semibold ${
                activeTabKey === "preview"
                  ? "btn-primary shadow-xs"
                  : "btn-outline-secondary"
              }`}
              onClick={() => setActiveTabKey("preview")}
            >
              <i className="ti ti-eye me-1" /> Live Preview
            </button>
          </div>
        </div>
      }
    >
      <div className="p-4">
        {activeTabKey === "edit" ? (
          <div className="customizer-edit-container">
            {/* Section 1: Order & File Reference */}
            <div className="card border-0 shadow-xs mb-4 rounded-3" style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}>
              <div className="card-header bg-transparent border-0 pt-3 pb-2 px-3">
                <h6 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2" style={{ fontSize: 14 }}>
                  <i className="ti ti-file-description text-primary" />
                  Order & File Metadata
                </h6>
              </div>
              <div className="card-body p-3 pt-0">
                <div className="row g-3">
                  <div className="col-md-3">
                    <label className="form-label small fw-semibold text-secondary mb-1">
                      Order Date (दिनांक)
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={formData.orderDate || ""}
                      onChange={(e) => handleInputChange("orderDate", e.target.value)}
                      placeholder="DD.MM.YYYY"
                    />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label small fw-semibold text-secondary mb-1">
                      Order No. (आदेश सं.)
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={formData.orderNo || ""}
                      onChange={(e) => handleInputChange("orderNo", e.target.value)}
                      placeholder="102"
                    />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label small fw-semibold text-secondary mb-1">
                      Financial Year
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={formData.financialYear || ""}
                      onChange={(e) => handleInputChange("financialYear", e.target.value)}
                      placeholder="2026-27"
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label small fw-semibold text-secondary mb-1">
                      Rules Ref. Date
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={formData.rulesReferenceDate || ""}
                      onChange={(e) => handleInputChange("rulesReferenceDate", e.target.value)}
                      placeholder="03.06.2025"
                    />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label small fw-semibold text-secondary mb-1">
                      Possession Days
                    </label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={formData.possessionDeadlineDays || 21}
                      onChange={(e) => handleInputChange("possessionDeadlineDays", Number(e.target.value))}
                      placeholder="21"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Section 2: Allotment Details */}
            <div className="card border-0 shadow-xs mb-4 rounded-3" style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}>
              <div className="card-header bg-transparent border-0 pt-3 pb-2 px-3">
                <h6 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2" style={{ fontSize: 14 }}>
                  <i className="ti ti-user-check text-success" />
                  Allottee & Quarter Details
                </h6>
              </div>
              <div className="card-body p-3 pt-0">
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label small fw-semibold text-secondary mb-1">
                      Employee Name (कर्मचारी का नाम)
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-sm fw-semibold text-dark"
                      value={formData.employeeName || ""}
                      onChange={(e) => handleInputChange("employeeName", e.target.value)}
                    />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label small fw-semibold text-secondary mb-1">
                      Employee ID
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={formData.employeeId || ""}
                      onChange={(e) => handleInputChange("employeeId", e.target.value)}
                    />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label small fw-semibold text-secondary mb-1">
                      Pool (पूल)
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={formData.pool || ""}
                      onChange={(e) => handleInputChange("pool", e.target.value)}
                      placeholder="OPN"
                    />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label small fw-semibold text-secondary mb-1">
                      Quarter Type
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={formData.quarterType || ""}
                      onChange={(e) => handleInputChange("quarterType", e.target.value)}
                      placeholder="II"
                    />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label small fw-semibold text-secondary mb-1">
                      Flat No. (फ्लैट सं.)
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-sm fw-bold text-success"
                      value={formData.flatNumber || ""}
                      onChange={(e) => handleInputChange("flatNumber", e.target.value)}
                      placeholder="411"
                    />
                  </div>
                  <div className="col-md-8">
                    <label className="form-label small fw-semibold text-secondary mb-1">
                      Location / Colony (स्थान)
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={formData.location || ""}
                      onChange={(e) => handleInputChange("location", e.target.value)}
                      placeholder="BGD / Bahadurgarh"
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-semibold text-secondary mb-1">
                      Category (श्रेणी)
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={formData.category || "नियमित"}
                      onChange={(e) => handleInputChange("category", e.target.value)}
                      placeholder="नियमित"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Section 3: Numbered Terms & Conditions (1 to 7) */}
            <div className="card border-0 shadow-xs mb-4 rounded-3" style={{ background: "#ffffff", border: "1px solid #e2e8f0" }}>
              <div className="card-header bg-transparent border-0 pt-3 pb-2 px-3 d-flex align-items-center justify-content-between">
                <h6 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2" style={{ fontSize: 14 }}>
                  <i className="ti ti-list-numbers text-warning" />
                  Terms & Conditions (नियम एवं शर्तें)
                </h6>
                <div className="d-flex align-items-center gap-2">
                  <span className="badge bg-light text-muted border px-2 py-1" style={{ fontSize: 11 }}>
                    Tag: <code>&#123;&#123;possessionDate&#125;&#125;</code> = {formData.possessionDeadlineDays || 21} days
                  </span>
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm py-0 px-2"
                    onClick={handleAddTerm}
                    style={{ fontSize: 12 }}
                  >
                    <i className="ti ti-plus me-1" /> Add Rule
                  </button>
                </div>
              </div>
              <div className="card-body p-3 pt-0">
                {(formData.terms || []).map((term, idx) => (
                  <div key={idx} className="d-flex align-items-start gap-2 mb-2">
                    <span
                      className="badge bg-light text-dark border d-flex align-items-center justify-content-center mt-1"
                      style={{ width: 26, height: 26, borderRadius: "50%", flexShrink: 0, fontWeight: 700 }}
                    >
                      {idx + 1}
                    </span>
                    <textarea
                      rows={2}
                      className="form-control form-control-sm"
                      style={{ fontSize: 13, resize: "vertical" }}
                      value={term}
                      onChange={(e) => handleTermChange(idx, e.target.value)}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm p-1 mt-1"
                      onClick={() => handleRemoveTerm(idx)}
                      title="Remove term"
                    >
                      <i className="ti ti-trash" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Section 4: Signatory Block */}
            <div className="card border-0 shadow-xs mb-4 rounded-3" style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}>
              <div className="card-header bg-transparent border-0 pt-3 pb-2 px-3">
                <h6 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2" style={{ fontSize: 14 }}>
                  <i className="ti ti-writing-sign text-info" />
                  Signatory Authority (हस्ताक्षरकर्ता)
                </h6>
              </div>
              <div className="card-body p-3 pt-0">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold text-secondary mb-1">
                      Signatory Hindi Name (नाम)
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={formData.signatoryName || ""}
                      onChange={(e) => handleInputChange("signatoryName", e.target.value)}
                      placeholder="(अशोक कुमार)"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold text-secondary mb-1">
                      Designation & Department (पदनाम)
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={formData.signatoryDesignation || ""}
                      onChange={(e) => handleInputChange("signatoryDesignation", e.target.value)}
                      placeholder="प्रबंधक/मा.सं./परि.एवं.अनु"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Section 5: Distribution List (प्रतिलिपि) */}
            <div className="card border-0 shadow-xs mb-2 rounded-3" style={{ background: "#ffffff", border: "1px solid #e2e8f0" }}>
              <div className="card-header bg-transparent border-0 pt-3 pb-2 px-3 d-flex align-items-center justify-content-between">
                <h6 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2" style={{ fontSize: 14 }}>
                  <i className="ti ti-copy text-secondary" />
                  Distribution List (प्रतिलिपि सूचि)
                </h6>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm py-0 px-2"
                  onClick={handleAddCopyItem}
                  style={{ fontSize: 12 }}
                >
                  <i className="ti ti-plus me-1" /> Add Recipient
                </button>
              </div>
              <div className="card-body p-3 pt-0">
                {(formData.copyToList || []).map((item, idx) => (
                  <div key={idx} className="d-flex align-items-center gap-2 mb-2">
                    <span
                      className="badge bg-light text-muted border d-flex align-items-center justify-content-center"
                      style={{ width: 24, height: 24, borderRadius: "50%", flexShrink: 0, fontSize: 11 }}
                    >
                      {idx + 1}
                    </span>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={item}
                      onChange={(e) => handleCopyItemChange(idx, e.target.value)}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm p-1"
                      onClick={() => handleRemoveCopyItem(idx)}
                      title="Remove recipient"
                    >
                      <i className="ti ti-trash" />
                    </button>
                  </div>
                ))}
                <div className="mt-3 pt-2 border-top">
                  <label className="form-label small fw-semibold text-secondary mb-1">
                    CC Line (सीसी / अन्य प्रति)
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={formData.ccTo || ""}
                    onChange={(e) => handleInputChange("ccTo", e.target.value)}
                    placeholder="सीसी/- सूचना पट्ट / व्यक्तिगत पत्रावली / कार्यालय प्रति"
                  />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="customizer-preview-container">
            <div className="d-flex align-items-center justify-content-between mb-3 bg-light p-2 px-3 rounded-3 border">
              <span className="small text-muted fw-semibold">
                <i className="ti ti-device-desktop me-1 text-primary" /> Live Document Rendering
              </span>
              <button
                className="btn btn-outline-secondary btn-sm px-2 py-1"
                onClick={fetchPreview}
                disabled={previewLoading}
                style={{ fontSize: 12 }}
              >
                <i className={`ti ti-refresh me-1 ${previewLoading ? "fa-spin" : ""}`} />
                Refresh Preview
              </button>
            </div>

            {previewLoading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary mb-3" role="status" />
                <h6 className="text-dark fw-bold">Generating Live Document Preview...</h6>
                <p className="text-muted small">Applying official DMRC styling, terms, and signatories</p>
              </div>
            ) : (
              <div
                className="preview-content-box p-3 rounded-3 border"
                style={{
                  background: "#f1f5f9",
                  maxHeight: "65vh",
                  overflowY: "auto",
                }}
              >
                <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal Footer */}
      <div className="d-flex align-items-center justify-content-between px-4 py-3 border-top bg-light rounded-bottom">
        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
            onClick={handleResetDefaults}
            title="Reset template back to standard DMRC rules"
          >
            <i className="ti ti-rotate-clockwise" />
            Reset Defaults
          </button>
          <button
            type="button"
            className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1"
            onClick={handleSaveAsTemplate}
            disabled={savingTemplate}
            title="Save custom terms & signatories to database"
          >
            {savingTemplate ? (
              <span className="spinner-border spinner-border-sm me-1" />
            ) : (
              <i className="ti ti-device-floppy" />
            )}
            Save as Template
          </button>
        </div>

        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn btn-light btn-sm px-3 border"
            onClick={onClose}
          >
            Close
          </button>
          <button
            type="button"
            className="btn btn-danger btn-sm px-4 fw-semibold shadow-sm d-flex align-items-center gap-2"
            style={{ backgroundColor: "#c2410c", borderColor: "#c2410c" }}
            onClick={handleDownloadPdf}
            disabled={downloadingPdf}
          >
            {downloadingPdf ? (
              <>
                <span className="spinner-border spinner-border-sm" role="status" />
                <span>Downloading PDF...</span>
              </>
            ) : (
              <>
                <i className="ti ti-download" style={{ fontSize: 16 }} />
                <span>Download Official PDF</span>
              </>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AllotmentLetterCustomizerModal;
